import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance } from '../config';
import './Premium.css';

const PLANS = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 60,
    period: 'month',
    description: 'One-time payment for 1 month of full premium access. Renew manually when it expires.',
    cta: 'Get 1 month access',
    popular: false
  },
  {
    id: 'yearly',
    name: 'Yearly',
    price: 899,
    period: 'year',
    description: 'Best value — one-time payment for 12 months of full premium access.',
    cta: 'Get 12 months access',
    popular: true
  }
];

const COMPARISON_FEATURES = [
  { label: 'Browse & search recipes',        free: true  },
  { label: 'Save favourite recipes',         free: true  },
  { label: 'Shopping list',                  free: true  },
  { label: 'Cupboard management',            free: true  },
  { label: 'Create your own recipes',        free: false },
  { label: 'Chat with Frankie (AI Buddy)',   free: false },
  { label: 'AI meal planning & suggestions', free: false },
  { label: 'Nutritional values on recipes',  free: false },
  { label: 'Ad-free experience',             free: false },
  { label: 'Export recipes & lists',         free: false },
];

export default function Premium() {
  const [subStatus, setSubStatus]         = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [activatingTrial, setActivatingTrial] = useState(false);
  const [error, setError]                 = useState('');
  const [successMsg, setSuccessMsg]       = useState('');

  const isLoggedIn = !!localStorage.getItem('userID');

  const fetchStatus = useCallback(async () => {
    try {
      if (!localStorage.getItem('userID')) { setLoadingStatus(false); return; }
      const res = await axiosInstance.get('/premium/status');
      setSubStatus(res.data);
    } catch {
      // silently ignore – user may not be logged in
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleActivateTrial = async () => {
    setError('');
    setActivatingTrial(true);
    try {
      await axiosInstance.post('/premium/activate-trial');
      setSuccessMsg('Your 14-day free trial is now active — enjoy Premium!');
      await fetchStatus();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not activate trial. Please try again.');
    } finally {
      setActivatingTrial(false);
    }
  };

  /* ── Status panel ───────────────────────────────────────────────────────── */
  const renderStatusPanel = () => {
    if (!isLoggedIn) {
      return (
        <div className="prem-panel prem-panel--none">
          <div className="prem-panel__icon">NEW</div>
          <div className="prem-panel__body">
            <h3>Try Premium free for 14 days</h3>
            <p>Create a free account and activate your trial — no credit card required.</p>
          </div>
          <Link to="/auth/register" className="prem-panel__btn">Get started free</Link>
        </div>
      );
    }

    if (loadingStatus) {
      return <div className="prem-panel-loading">Loading your subscription…</div>;
    }

    if (!subStatus) return null;

    const { status, plan, subscriptionEndDate, trialEndDate, trialDaysLeft } = subStatus;

    if (status === 'none') {
      return (
        <div className="prem-panel prem-panel--none">
          <div className="prem-panel__icon">TRIAL</div>
          <div className="prem-panel__body">
            <h3>Activate your free 14-day trial</h3>
            <p>Get instant access to all premium features — no credit card required.</p>
          </div>
          <button
            className="prem-panel__btn"
            onClick={handleActivateTrial}
            disabled={activatingTrial}
          >
            {activatingTrial ? 'Activating...' : 'Activate free trial'}
          </button>
        </div>
      );
    }

    if (status === 'trial') {
      const days   = trialDaysLeft ?? 0;
      const endStr = trialEndDate ? new Date(trialEndDate).toLocaleDateString('en-ZA') : '';
      const pct    = Math.max(4, Math.min(100, Math.round(((14 - days) / 14) * 100)));
      return (
        <div className="prem-panel prem-panel--trial">
          <div className="prem-panel__icon">TRIAL</div>
          <div className="prem-panel__body">
            <h3>
              Free Trial —{' '}
              <strong>{days} day{days !== 1 ? 's' : ''} remaining</strong>
            </h3>
            {endStr && <p>Trial ends on {endStr}. Subscribe below to keep access.</p>}
            <div className="prem-trial-bar">
              <div className="prem-trial-bar__fill" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <Link to="/premium/checkout?plan=monthly" className="prem-panel__btn prem-panel__btn--green">
            Upgrade now
          </Link>
        </div>
      );
    }

    if (status === 'active') {
      const expiresStr = subscriptionEndDate
        ? new Date(subscriptionEndDate).toLocaleDateString('en-ZA')
        : '';
      const planLabel = plan === 'yearly' ? '12-Month' : 'Monthly';
      return (
        <div className="prem-panel prem-panel--active">
          <div className="prem-panel__icon">LIVE</div>
          <div className="prem-panel__body">
            <h3>Premium {planLabel} — Active</h3>
            {expiresStr && (
              <p>Access valid until: <strong>{expiresStr}</strong></p>
            )}
            <p className="prem-no-renew-note">No automatic renewal — you can pay again when your access expires.</p>
          </div>
          <span className="prem-active-badge">Active</span>
        </div>
      );
    }

    if (status === 'cancelled') {
      return (
        <div className="prem-panel prem-panel--expired">
          <div className="prem-panel__icon">INFO</div>
          <div className="prem-panel__body">
            <h3>Subscription cancelled</h3>
            <p>Subscribe to a plan below to regain full premium access.</p>
          </div>
        </div>
      );
    }

    if (status === 'expired') {
      return (
        <div className="prem-panel prem-panel--expired">
          <div className="prem-panel__icon">END</div>
          <div className="prem-panel__body">
            <h3>Your free trial has ended</h3>
            <p>Subscribe to a plan below to continue enjoying premium features.</p>
          </div>
        </div>
      );
    }

    return null;
  };

  const showPlanCards = !subStatus || subStatus.status !== 'active';

  return (
    <div className="premium-page">

      {/* Hero */}
      <section className="premium-hero">
        <h1>WhatToMake Premium</h1>
        <p className="premium-hero-sub">
          Cook smarter with AI assistance, personalised meal plans, and full nutritional insights.
        </p>
      </section>

      {/* Global alerts */}
      {error && (
        <div className="prem-alert prem-alert--error">
          {error}
          <button onClick={() => setError('')} className="prem-alert__close">×</button>
        </div>
      )}
      {successMsg && (
        <div className="prem-alert prem-alert--success">
          {successMsg}
          <button onClick={() => setSuccessMsg('')} className="prem-alert__close">×</button>
        </div>
      )}

      {/* Status panel */}
      {renderStatusPanel()}

      {/* Feature comparison */}
      <section className="prem-comparison">
        <h2>What's included</h2>
        <div className="prem-comparison-table-wrap">
          <table className="prem-comparison-table">
            <thead>
              <tr>
                <th className="prem-col-feature">Feature</th>
                <th className="prem-col-free">Free</th>
                <th className="prem-col-premium">Premium</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_FEATURES.map((f, i) => (
                <tr key={i} className={!f.free ? 'prem-row-highlight' : ''}>
                  <td>{f.label}</td>
                  <td className="prem-cell-check">
                    {f.free
                      ? <span className="prem-check prem-check--yes">✓</span>
                      : <span className="prem-check prem-check--no">—</span>}
                  </td>
                  <td className="prem-cell-check">
                    <span className="prem-check prem-check--yes">✓</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Plan cards */}
      {showPlanCards && (
        <section className="premium-plans">
          <h2 className="premium-plans-heading">Choose a plan</h2>
          <div className="premium-plans-grid">
            {PLANS.map((plan) => (
              <article key={plan.id} className={`premium-card ${plan.popular ? 'premium-card-popular' : ''}`}>
                {plan.popular && <span className="premium-badge">Best value</span>}
                <h2>{plan.name}</h2>
                <div className="premium-price">
                  <span className="premium-currency">R</span>
                  <span className="premium-amount">{plan.price}</span>
                  <span className="premium-period">/ {plan.period}</span>
                </div>
                <p className="premium-desc">{plan.description}</p>
                <Link to={`/premium/checkout?plan=${plan.id}`} className="premium-cta">
                  {plan.cta}
                </Link>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="premium-faq">
        <h2>Frequently asked questions</h2>
        <div className="premium-faq-item">
          <h3>Do I need a credit card for the free trial?</h3>
          <p>No. Activate your 14-day free trial directly from this page — no card required. You only provide payment details when you choose to subscribe after the trial.</p>
        </div>
        <div className="premium-faq-item">
          <h3>How does billing work?</h3>
          <p>Each plan is a <strong>one-time payment</strong> for a fixed access period — 1 month or 12 months. There is no automatic renewal. When your access expires, simply pay again to continue enjoying premium features.</p>
        </div>
        <div className="premium-faq-item">
          <h3>Do you offer discounts?</h3>
          <p>Yes. If you have a promo code, enter it on the checkout page to get a percentage off your payment.</p>
        </div>
        <div className="premium-faq-item">
          <h3>Do I need to cancel?</h3>
          <p>No. Because there is no recurring billing, nothing will be charged after your access period ends. Your account simply returns to the free tier automatically.</p>
        </div>
        <div className="premium-faq-item">
          <h3>What happens when my trial ends?</h3>
          <p>Your account moves to the free tier automatically. Choose any plan below to continue without interruption.</p>
        </div>
      </section>
    </div>
  );
}
